import { getFilter } from "core-integration/src/store/elements/reducers";
import { IElement } from "core-integration/src/store/elements/types";
import { IElementPriceChange } from "core-integration/src/store/stats/types";
import { SortDescriptor } from "react-aria-components";
import { getPriceChangeStatus } from "../utils";

export type PriceChangeProgressFilter =
  "riseAndDrop" | "rise" | "drop" | "locked";

export type PriceChangeElementFilter =
  "all" | "myTeam" | "watchlist" | `et_${number}`;

export type PriceChangeSortColumn =
  | "player"
  | "status"
  | "progress"
  | "progressMagnitude"
  | "predictedProgress"
  | "ownership"
  | "currentPrice"
  | "purchasePrice"
  | "sellingPrice";

type SavedPicksByElement = Record<
  number,
  { purchase_price: number; selling_price: number }
>;

interface ElementFilterContext {
  savedPicksByElement: SavedPicksByElement;
  watched: number[];
}

export const matchesElementFilter = (
  element: IElement,
  filter: PriceChangeElementFilter,
  { savedPicksByElement, watched }: ElementFilterContext,
) => {
  if (filter === "all") return true;
  if (filter === "myTeam") return Boolean(savedPicksByElement[element.id]);
  if (filter === "watchlist") return watched.includes(element.code);
  return element.element_type === getFilter(filter).element_type;
};

const matchesProgressDirection = (
  value: number,
  filter: PriceChangeProgressFilter,
) => {
  if (filter === "rise") return value > 0;
  if (filter === "drop") return value < 0;
  return true;
};

const sortByValue = <T>(
  items: T[],
  getValue: (item: T) => number | string,
  direction: SortDescriptor["direction"],
) =>
  [...items].sort((a, b) => {
    const aValue = getValue(a);
    const bValue = getValue(b);
    const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    return direction === "descending" ? -comparison : comparison;
  });

// Filters the table to the players matching the selected progress option.
// `rise`/`drop` restrict to positive/negative progress and `locked` shows only
// players whose price is currently locked. Every other option shows all players.
export const filterByProgress = (
  elements: IElement[],
  filter: PriceChangeProgressFilter,
) =>
  elements.filter((element) =>
    filter === "locked"
      ? Boolean(element.price_change_locked_until)
      : matchesProgressDirection(
          Number(element.price_change_percent) || 0,
          filter,
        ),
  );

// Sorts the table by a clickable column header. Purchase/selling price live on
// the manager's saved picks rather than the element, so they are looked up via
// savedPicksByElement.
export const sortElementsByColumn = (
  elements: IElement[],
  { column, direction }: SortDescriptor,
  savedPicksByElement: SavedPicksByElement,
) => {
  const getValue = (element: IElement): number | string => {
    switch (column as PriceChangeSortColumn) {
      case "player":
        return element.web_name;
      case "status":
        return getPriceChangeStatus(element);
      case "progress":
        return Number(element.price_change_percent) || 0;
      case "progressMagnitude":
        // Default sort: surface the biggest movers regardless of sign.
        return Math.abs(Number(element.price_change_percent) || 0);
      case "predictedProgress":
        return (
          Number(element.price_change_projections?.[0]?.projected_percent) || 0
        );
      case "ownership":
        return element.transfers_in_event - element.transfers_out_event;
      case "currentPrice":
        return element.now_cost;
      case "purchasePrice":
        return savedPicksByElement[element.id]?.purchase_price ?? 0;
      case "sellingPrice":
        return savedPicksByElement[element.id]?.selling_price ?? 0;
      default:
        return 0;
    }
  };

  return sortByValue(elements, getValue, direction);
};

export type PriceChangeHistorySortColumn =
  "player" | "date" | "change" | "previousPrice" | "newPrice";

export const getPriceChangeDelta = (change: IElementPriceChange) =>
  change.cost_after - change.cost_before;

interface PriceChangeHistoryFilters extends ElementFilterContext {
  elementsById: Record<number, IElement>;
  elementFilter: PriceChangeElementFilter;
  progressFilter: PriceChangeProgressFilter;
}

export const filterPriceChangeHistory = (
  changes: IElementPriceChange[],
  {
    elementsById,
    elementFilter,
    progressFilter,
    ...elementFilterContext
  }: PriceChangeHistoryFilters,
) =>
  changes.filter((change) => {
    const element = elementsById[change.element];
    return (
      Boolean(element) &&
      matchesElementFilter(element, elementFilter, elementFilterContext) &&
      matchesProgressDirection(getPriceChangeDelta(change), progressFilter)
    );
  });

export const sortPriceChangeHistoryByColumn = (
  changes: IElementPriceChange[],
  { column, direction }: SortDescriptor,
  elementsById: Record<number, IElement>,
) => {
  const getValue = (change: IElementPriceChange): number | string => {
    switch (column as PriceChangeHistorySortColumn) {
      case "player":
        return elementsById[change.element]?.web_name ?? "";
      case "date":
        return new Date(change.changed_at).getTime();
      case "change":
        return getPriceChangeDelta(change);
      case "previousPrice":
        return change.cost_before;
      case "newPrice":
        return change.cost_after;
      default:
        return 0;
    }
  };

  return sortByValue(changes, getValue, direction);
};
